import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { testimonialsData } from '../mock';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

export const Testimonials = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? testimonialsData.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === testimonialsData.length - 1 ? 0 : prev + 1));
  };

  return (
    <section ref={containerRef} className="py-32 bg-gradient-to-b from-farm-brown/20 to-farm-dark">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-farm-cream mb-6">
            What Our <span className="text-farm-gold">Partners Say</span>
          </h2>
          <p className="text-xl text-farm-cream/70">
            Trusted by industry leaders worldwide
          </p>
        </motion.div>

        <div className="relative">
          {/* Testimonial Cards */}
          <div className="overflow-hidden">
            <motion.div
              animate={{ x: `-${activeIndex * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              className="flex"
            >
              {testimonialsData.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="min-w-full px-4"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.6 }}
                    className="bg-farm-dark/50 backdrop-blur-sm border border-farm-green/20 rounded-3xl p-12 shadow-2xl"
                  >
                    {/* Stars */}
                    <div className="flex gap-1 mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-6 h-6 fill-farm-gold text-farm-gold" />
                      ))}
                    </div>

                    {/* Quote */}
                    <p className="text-2xl text-farm-cream/90 leading-relaxed mb-8 italic">
                      "{testimonial.content}"
                    </p>

                    {/* Author */}
                    <div className="flex items-center gap-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-farm-gold"
                      />
                      <div>
                        <div className="text-xl font-semibold text-farm-cream">
                          {testimonial.name}
                        </div>
                        <div className="text-farm-cream/60">{testimonial.role}</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-12">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handlePrev}
              className="w-12 h-12 bg-farm-green hover:bg-farm-gold rounded-full flex items-center justify-center text-farm-cream transition-colors duration-300 shadow-lg"
            >
              <ChevronLeft size={24} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleNext}
              className="w-12 h-12 bg-farm-green hover:bg-farm-gold rounded-full flex items-center justify-center text-farm-cream transition-colors duration-300 shadow-lg"
            >
              <ChevronRight size={24} />
            </motion.button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {testimonialsData.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex
                    ? 'w-8 bg-farm-gold'
                    : 'w-2 bg-farm-cream/30 hover:bg-farm-cream/50'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};